import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Logger from 'observability/Logger';

import { getQuestionFormNodes } from 'common/entities/formNode/selectors';
import { getFormValues } from '../../../../selectors/form';
import { withFormId } from '../../../FormIdContext';

import FormErrorMessage from '../../../errors/FormErrorMessage';
import Loader from './Loader';
import eventOPSClientLib from './eventOPSClientLib';

const LOG = new Logger('EventOps2Core');

let globalIdCount = 0;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

/**
 *
 * @param {bool} loaded - if the widget JS code loaded.
 * @param {bool} initialized - if the widget is ready for use.
 * @return {string}
 */
function getLoaderLabel(loaded, initialized) {
    if (!loaded) {
        return 'Loading...';
    } else if (!initialized) {
        return 'Finding available resources...';
    }
    return '';
}

/**
 * Maps nodes and answers to the format that the widget expects.
 *
 * @param {Object} questionFormNodes - all question nodes in the form.
 * @param {Object} formValues - object where key is node id and value is current answer.
 */
const mapQuestionFormNodesToFormData = (questionFormNodes, formValues) =>
    questionFormNodes.map((node) => ({
        name: node.code,
        label: node.label,
        inputType: node.inputType,
        answer: formValues[node.id],
    }));

/**
 * Core EventOPS Resouces question, integrated with the widget.
 */
class EventOps2Core extends React.Component {
    static propTypes = {
        clientId: PropTypes.string.isRequired,
        clientSecret: PropTypes.string.isRequired,
        companyToken: PropTypes.string.isRequired,

        startDateTime: PropTypes.string,
        endDateTime: PropTypes.string,
        timeZone: PropTypes.string,
        location: PropTypes.string,

        resourceType: PropTypes.string.isRequired,
        widgetUrl: PropTypes.string.isRequired,

        /** list of question nodes of the form */
        questionFormNodes: PropTypes.arrayOf(PropTypes.object).isRequired,
        /** all answers to other questions in the form */
        formValues: PropTypes.object.isRequired,

        onChange: PropTypes.func.isRequired,
        onLoad: PropTypes.func,

        /** Function that returns window. Only for unit testing to mock it. */
        _getWindow: PropTypes.func,
    };

    static defaultProps = {
        onLoad: undefined,
        startDateTime: undefined,
        endDateTime: undefined,
        location: undefined,
        timeZone: null,
        _getWindow: () => window,
    };

    constructor(props) {
        super(props);

        this.window = this.props._getWindow();

        this.eventOPS = eventOPSClientLib(this.props._getWindow());

        this.state = {
            loaded: this.eventOPS.isLoaded(),
            initialized: this.eventOPS.isReady(),
            elementId: `event-ops-${globalIdCount}`,
            widgetIdentifier: `w${globalIdCount}`,
            error: null,
        };
        globalIdCount += 1;

        if (!this.state.loaded) {
            this.loadCheck = setInterval(this.updateLoadState, 500);
        }
        if (!this.state.initialized) {
            this.initializeCheck = setInterval(this.updateInitializedState, 500);
        }
    }

    componentDidMount() {
        if (this.state.loaded) {
            this.initWidget();
        }
        if (this.state.initialized) {
            this.renderWidget();
        }
    }

    componentWillUnmount() {
        clearInterval(this.loadCheck);
        if (this.widgetIdentifier) {
            this.eventOPS.destroy(this.widgetIdentifier, this.props.resourceType);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.loaded && !prevState.loaded) {
            this.initWidget();
        }

        if (this.state.initialized && !prevState.initialized) {
            this.renderWidget();
        }

        if (
            this.eventOPS.isReady() &&
            (prevProps.startDateTime !== this.props.startDateTime ||
                prevProps.endDateTime !== this.props.endDateTime ||
                prevProps.timeZone !== this.props.timeZone ||
                prevProps.location !== this.props.location ||
                prevProps.resourceType !== this.props.resourceType ||
                prevProps.questionFormNodes !== this.props.questionFormNodes ||
                prevProps.formValues !== this.props.formValues)
        ) {
            this.updateWidgetInformation();
        }
    }

    /**
     * @param {bool} isValid
     * @param {Object[]} lockedResources
     */
    handleChange = (isValid, lockedResources) => {
        this.setState({ error: null });
        this.props.onChange(isValid ? lockedResources : null);
    };

    handleError = (status) => {
        this.setState({ error: status });
    };

    updateLoadState = () => {
        if (this.eventOPS.isLoaded()) {
            this.setState({ loaded: true });
            clearInterval(this.loadCheck);
        }
    };

    updateInitializedState = () => {
        if (this.eventOPS.isReady()) {
            this.setState({ initialized: true });
            clearInterval(this.initializeCheck);
        }
    };

    initWidget() {
        try {
            this.eventOPS.init({
                client_id: this.props.clientId,
                client_secret: this.props.clientSecret,
                company_token: this.props.companyToken,
                widget_url: this.props.widgetUrl,
                grant_type: 'password',
            });
        } catch (error) {
            LOG.error('Error initializing.', error);
            this.setState({ error: 'Error initializing.' });
        }
    }

    updateWidgetInformation() {
        try {
            this.eventOPS.updateInformation(
                this.state.widgetIdentifier,
                {
                    onChange: this.handleChange,
                    startDate: this.props.startDateTime,
                    onError: this.handleError,
                    endDate: this.props.endDateTime,
                    timezone: this.props.timeZone,
                    location: this.props.location,
                    form: this.getFormDataForWidget(),
                },
                this.props.resourceType,
            );
        } catch (error) {
            LOG.error('Error updating information.', error);
            this.setState({ error: 'Error updating information.' });
        }
    }

    renderWidget() {
        try {
            this.eventOPS.create(
                this.state.widgetIdentifier,
                this.state.elementId,
                {
                    onChange: this.handleChange,
                    onError: this.handleError,
                    startDate: this.props.startDateTime,
                    endDate: this.props.endDateTime,
                    timezone: this.props.timeZone,
                    location: this.props.location,
                    form: this.getFormDataForWidget(),
                },
                this.props.resourceType,
            );
        } catch (error) {
            LOG.error('Error creating resources view.', error);
            this.setState({ error: 'Error creating resources view.' });
        }

        if (this.props.onLoad) {
            this.props.onLoad();
        }
    }

    getFormDataForWidget() {
        const { questionFormNodes, formValues } = this.props;
        return mapQuestionFormNodesToFormData(questionFormNodes, formValues);
    }

    render() {
        const { loaded, initialized, elementId, error } = this.state;

        return (
            <Container id={elementId}>
                {error && <FormErrorMessage errorType={`Error: ${error}`} />}
                {(!loaded || !initialized) && <Loader label={getLoaderLabel(loaded, initialized)} />}
            </Container>
        );
    }
}

const mapStateToProps = (state, props) => {
    const { formId } = props;
    const questionFormNodes = getQuestionFormNodes(state, formId);
    const formValues = getFormValues(state, formId);

    return { questionFormNodes, formValues };
};

export default withFormId(connect(mapStateToProps, undefined)(EventOps2Core));
